import React from 'react'
import {useTranslation} from 'react-i18next'
import {getTimeZoneNotice} from '@common/utils/dates'

import {
  Grid,
  Column,
  RescheduleFormCard,
  ButtonsRow,
  OrangeButton,
} from './AppointmentManagementPage.styled'
import {CalendarPanel} from './CalendarPanel'
import {DayTimePanel} from './DayTimePanel'

import {useMonthNavigation} from '~/hooks/useMonthNavigation'
import {useWeekdayShorts} from '~/hooks/useWeekdayShorts'
import {Typography} from '~/components/general/typography/Typography'
import {Container} from '~/components/general/container/Container'
import {TextArea} from '~/components/general/textArea/TextArea'

type ReschedulePanelProps = {
  currentYyMm: string
  onMonthChange: (yyMm: string) => void
  cells: {date: Date; outside: boolean}[]
  slotsByDate: Map<string, string[]>
  selectedDate: string | null
  selectedTime: string | null
  onSelectDate: (dateKey: string) => void
  onSelectTime: (time: string) => void
  isLoading: boolean
  timeZoneId?: string
  comment: string
  onChangeComment: (value: string) => void
  onBack: () => void
  onConfirm: () => void
}

export const ReschedulePanel = ({
  currentYyMm,
  onMonthChange,
  cells,
  slotsByDate,
  selectedDate,
  selectedTime,
  onSelectDate,
  onSelectTime,
  isLoading,
  timeZoneId,
  comment,
  onChangeComment,
  onBack,
  onConfirm,
}: ReschedulePanelProps) => {
  const {t} = useTranslation()

  const weekdayShorts = useWeekdayShorts()

  const {gotoPrevMonth, gotoNextMonth, monthLabel, disablePrev} =
    useMonthNavigation({
      currentYyMm,
      onMonthChange,
    })

  const timeZoneNotice = getTimeZoneNotice(timeZoneId, t)
  const noAvailabilityText = t(
    'There is no availability on this date. Please choose another one.',
  )

  return (
    <>
      <Grid>
        <Column>
          <CalendarPanel
            dateTitle={t('DATE')}
            monthLabel={monthLabel}
            weekdayShorts={weekdayShorts}
            cells={cells}
            slotsByDate={slotsByDate}
            selectedDate={selectedDate}
            onSelectDate={onSelectDate}
            disablePrev={disablePrev}
            onPrevMonth={gotoPrevMonth}
            onNextMonth={gotoNextMonth}
          />
        </Column>
        <Column>
          <DayTimePanel
            title={t('TIME')}
            selectedDate={selectedDate}
            selectedDateSlots={
              selectedDate ? slotsByDate.get(selectedDate) || [] : []
            }
            selectedTime={selectedTime}
            onSelectTime={onSelectTime}
            isLoading={isLoading}
            timeZoneNotice={timeZoneNotice}
            noAvailabilityText={noAvailabilityText}
          />
        </Column>
      </Grid>
      <RescheduleFormCard>
        <Container direction="column" spacing={2}>
          <Typography weight="bold">{t('Add a comment')}</Typography>
          <TextArea
            height="120px"
            placeholder={t('Write your reason for rescheduling (optional)')}
            value={comment}
            onChange={(e) => onChangeComment(e.target.value)}
          />
          <ButtonsRow>
            <OrangeButton color="secondary" onClick={onBack}>
              {t('Back')}
            </OrangeButton>
            <OrangeButton disabled={!selectedTime} onClick={onConfirm}>
              {t('Confirm')}
            </OrangeButton>
          </ButtonsRow>
        </Container>
      </RescheduleFormCard>
    </>
  )
}
